import { ONE_TIME_PAY, SERVICE_RECOVERY } from '../helpers/apis';
import { SERVICE_RECOVERY_ENROLLED, SERVICE_RECOVERY_FAILED } from '../helpers/channelTracking';
import { COTTON_ONE_TIME_PAY, COTTON_SERVICE_RECOVERY } from '../helpers/cottonApis';
import { getErrorFromResponse } from '../helpers/errors';
import { isCottonSession as getCottonSession } from '../helpers/featureFlags/cotton';
import { buildInstrument } from '../helpers/payments';
import { buildPaymentData } from '../helpers/serviceRecovery';
import apiClient from '../middleware/apiClient';
import { dispatchSendTransaction, dispatchSendErrorEvent } from './adobe';
import { sendChannelTracking } from './channelTracking';
import {
  POST_ONE_TIME_PAY,
  POST_ONE_TIME_PAY_FAILURE,
  POST_ONE_TIME_PAY_SUCCESS,
  REVIEW_PAYMENT,
  SUBMIT_PAYMENT,
  SUBMIT_PAYMENT_FAILURE,
  SUBMIT_PAYMENT_SUCCESS,
} from './payment';

export const GET_SERVICE_RECOVERY = 'GET_SERVICE_RECOVERY';
export const GET_SERVICE_RECOVERY_SUCCESS = 'GET_SERVICE_RECOVERY_SUCCESS';
export const GET_SERVICE_RECOVERY_FAILURE = 'GET_SERVICE_RECOVERY_FAILURE';

export const getServiceRecovery = () => async (dispatch) => {
  const isCottonSession = getCottonSession();
  const endpoint = isCottonSession ? COTTON_SERVICE_RECOVERY : SERVICE_RECOVERY;

  try {
    const response = await dispatch({
      type: GET_SERVICE_RECOVERY,
      payload: apiClient.fetch(endpoint),
    }).payload;

    dispatch({
      type: GET_SERVICE_RECOVERY_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch({
      type: GET_SERVICE_RECOVERY_FAILURE,
      payload: {
        message: error?.data?.message,
        error: error?.status,
      },
    });
  }
};

const dispatchReviewPayment = payload => ({
  type: REVIEW_PAYMENT,
  payload,
});

export const setReviewData = (formData, email) => {
  const { instrument } = buildInstrument(formData);

  return {
    ...formData,
    instrument,
    email,
  };
};

export const reviewPayment = values => async (dispatch, getState) => {
  const {
    user: { user: { preferredEmail, contactEmail } } = {},
  } = getState();

  const reviewData = setReviewData(values, preferredEmail || contactEmail);

  return dispatch(dispatchReviewPayment(reviewData));
};

const postOneTimePay = data => async (dispatch) => {
  const isCottonSession = getCottonSession();
  const endpoint = isCottonSession ? COTTON_ONE_TIME_PAY : ONE_TIME_PAY;
  try {
    const response = await dispatch({
      type: POST_ONE_TIME_PAY,
      payload: apiClient.fetch(endpoint, { method: 'POST', body: JSON.stringify(data) }),
    }).payload;

    dispatch({
      type: POST_ONE_TIME_PAY_SUCCESS,
      payload: response,
    });
    dispatch(sendChannelTracking({
      id: SERVICE_RECOVERY_ENROLLED,
      interactionType: 'BILLING_PAYMENTS',
      contextHubMigratedSource: true,
    }));
    return response;
  } catch (error) {
    dispatch({
      type: POST_ONE_TIME_PAY_FAILURE,
      payload: error,
    });
    dispatch(sendChannelTracking({
      id: SERVICE_RECOVERY_FAILED,
      error,
      interactionType: 'BILLING_PAYMENTS',
      contextHubMigratedSource: true,
    }));
    throw error;
  }
};

const dispatchSubmitPayment = payload => ({
  type: SUBMIT_PAYMENT,
  payload,
});

const dispatchSubmitPaymentSuccess = payload => ({
  type: SUBMIT_PAYMENT_SUCCESS,
  payload,
});

const dispatchSubmitPaymentFailure = error => ({
  type: SUBMIT_PAYMENT_FAILURE,
  payload: error,
});

export const submitPayment = ({
  transactionMetadata,
}) => async (dispatch, getState) => {
  const {
    contextHub: { contextHub } = {},
    payment: { review },
  } = getState();

  dispatch(dispatchSubmitPayment(review));

  try {
    const paymentData = buildPaymentData(
      review, contextHub, transactionMetadata,
    );

    const response = await dispatch(postOneTimePay(paymentData));

    if (!response) {
      throw new Error('Error submitting payment');
    }

    // format the response
    const {
      confirmationNumber, authorizationNumber,
    } = response;
    const confirm = {
      ...review,
      ...response,
      // Include the formatted confirmation numbers.
      confirmationNumber: confirmationNumber && `#${confirmationNumber}`,
      authorizationNumber: authorizationNumber && `#${authorizationNumber}`,
    };

    dispatch(dispatchSendTransaction({
      name: 'self service transaction',
      action: 'self service transaction',
      transaction: 'billing:singlepay:today:payment:confirm',
    }));

    dispatch({
      type: SUBMIT_PAYMENT_SUCCESS,
      payload: confirm,
    });
    return confirm;
  } catch (error) {
    const errorDetail = getErrorFromResponse(error);

    dispatch(dispatchSubmitPaymentFailure(errorDetail));
    dispatch(dispatchSendErrorEvent(errorDetail));
    throw error;
  }
};

export const setConfirmForApplePay = ({ confirmationNumber, authCode }) => (dispatch, getState) => {
  const { serviceRecovery: { review } = {} } = getState();
  return dispatch(dispatchSubmitPaymentSuccess({
    ...review,
    confirmationNumber: confirmationNumber && `#${confirmationNumber}`,
    authCode: authCode && `#${authCode}`,
  }));
};
